import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  InputBase,
  styled,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Popover
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { PropertyOption } from "../../../../shared/interfaces/property";
import { formatDate } from "../../../../shared/utils/formatter";
import { getEventColor, parseISODate } from "../../../../shared/utils/eventCalendarUtils";
import StyledButton from "../../../../shared/components/styledButton";

const SearchContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: "1px solid #E0E3E7",
  borderRadius: "8px",
  padding: "4px 12px",
  marginBottom: "16px",
  width: "100%",
}));

const StyledInputBase = styled(InputBase)({
  width: "100%",
  marginLeft: "8px",
  "& .MuiInputBase-input::placeholder": {
    color: "#9E9E9E",
    opacity: 1,
  },
});

const CalendarGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  borderTop: "1px solid #E0E3E7",
  borderLeft: "1px solid #E0E3E7",
  "& > div": {
    borderRight: "1px solid #E0E3E7",
    borderBottom: "1px solid #E0E3E7",
  },
});

interface EventCalendarModalProps {
  open: boolean;
  onClose: () => void;
  targetProperty?: PropertyOption;
  initialDate?: Date | null;
  onSelectEvent: (event: any, selectedDate: Date) => void;
}

export const EventCalendarModal = ({
  open,
  onClose,
  targetProperty,
  initialDate,
  onSelectEvent,
}: EventCalendarModalProps) => {
  const [currentDate, setCurrentDate] = useState<Date>(
    initialDate || new Date(2026, 0, 1)
  );
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [selectedDayEvents, setSelectedDayEvents] = useState<{ date: Date; events: any[] } | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [fetchedQueries, setFetchedQueries] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSearchValue("");
      setAppliedSearch("");
      handlePopoverClose();
      if (initialDate) {
        setCurrentDate(new Date(initialDate));
      }
    }
  }, [open, initialDate]);

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentDate]);

  const eventQueryParams = useMemo(() => {
    if (!targetProperty || calendarDays.length === 0) return "";

    const gridStartDate = calendarDays[0].date;
    const gridEndDate = calendarDays[calendarDays.length - 1].date;

    return new URLSearchParams({
      country: targetProperty.country || "",
      state: targetProperty.state || "",
      city: targetProperty.city || "",
      propertyCode: String(targetProperty.code || ""),
      startDate: formatDate(gridStartDate),
      endDate: formatDate(gridEndDate),
      take: "500",
      ...(appliedSearch ? { name: appliedSearch } : {}),
    }).toString();
  }, [targetProperty, calendarDays, appliedSearch]);

  useEffect(() => {
    if (open && eventQueryParams) {
      setFetchedQueries((prev) => new Set(prev).add(eventQueryParams));
    }
  }, [open, eventQueryParams]);

  const shouldFetch = open || fetchedQueries.has(eventQueryParams);

  const { data: eventsResponse, loading: loadingEvents } = useQuery(
    `${process.env.REACT_APP_API_URL}/events/by-location?${eventQueryParams}`,
    {},
    null,
    { enabled: shouldFetch && !!eventQueryParams }
  );

  const eventsList = useMemo(() => {
    return (eventsResponse as any)?.data?.items || [];
  }, [eventsResponse]);

  const handlePrevMonth = () => {
    handlePopoverClose();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    handlePopoverClose();
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleSearch = () => setAppliedSearch(searchValue);

  const getEventsForDay = (cellDate: Date) => {
    const target = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();

    return eventsList.filter((event: any) => {
      const startObj = parseISODate(event.startDate);
      const endObj = parseISODate(event.endDate || event.startDate);

      if (!startObj || !endObj) return false;

      const start = new Date(startObj.getFullYear(), startObj.getMonth(), startObj.getDate()).getTime();
      const end = new Date(endObj.getFullYear(), endObj.getMonth(), endObj.getDate()).getTime();

      return target >= start && target <= end;
    });
  };

  const handleDayClick = (event: React.MouseEvent<HTMLElement>, date: Date, dayEvents: any[]) => {
    if (dayEvents.length > 0) {
      setAnchorEl(event.currentTarget);
      setSelectedDayEvents({ date, events: dayEvents });
      setSelectedEventId(dayEvents[0].id);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedDayEvents(null);
  };

  const confirmSelection = () => {
    const selectedEvent = selectedDayEvents?.events.find((e) => e.id === selectedEventId);
    if (selectedEvent && selectedDayEvents) {
      onSelectEvent(selectedEvent, selectedDayEvents.date);
      handlePopoverClose();
    }
  };

  const isPopoverOpen = Boolean(anchorEl);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0px 12px 40px rgba(0,0,0,0.12)",
          maxWidth: "750px"
        },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>
            Property Events
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: "grey.100" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <SearchContainer>
          <StyledInputBase
            placeholder="Search events"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (e.target.value === "" && appliedSearch) setAppliedSearch("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <IconButton size="small" onClick={handleSearch}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </SearchContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {currentDate.toLocaleString("default", { month: "long" })} {currentDate.getFullYear()}
          </Typography>
          <Box>
            <IconButton onClick={handlePrevMonth} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={handleNextMonth} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {loadingEvents ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={6} minHeight={250}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            <CalendarGrid sx={{ borderTop: "none", borderLeft: "none", mb: 0.5 }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <Typography key={day} align="center" variant="caption" fontWeight={600} color="textSecondary" sx={{ border: "none !important" }}>
                  {day}
                </Typography>
              ))}
            </CalendarGrid>

            <CalendarGrid>
              {calendarDays.map((day, index) => {
                const dayEvents = getEventsForDay(day.date);
                const isSelectedDay = selectedDayEvents?.date.toDateString() === day.date.toDateString();

                return (
                  <Box
                    key={index}
                    onClick={(e) => handleDayClick(e, day.date, dayEvents)}
                    sx={{
                      minHeight: 65,
                      p: 0.5,
                      minWidth: 0,
                      overflow: "hidden",
                      cursor: dayEvents.length > 0 ? "pointer" : "default",
                      bgcolor: isSelectedDay ? "#E3F2FD" : day.isCurrentMonth ? "#fff" : "#FAFAFA",
                      "&:hover": { bgcolor: dayEvents.length > 0 && !isSelectedDay ? "#F5F5F5" : undefined },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Typography
                      align="center"
                      variant="body2"
                      color={day.isCurrentMonth ? "textPrimary" : "textSecondary"}
                      sx={{ mb: 0.25, fontWeight: dayEvents.length > 0 ? 700 : 400, fontSize: "0.8rem" }}
                    >
                      {day.date.getDate()}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      {dayEvents.slice(0, 3).map((event: any, i: number) => (
                        <Box
                          key={`${event.id}-${i}`}
                          sx={{
                            bgcolor: getEventColor(event),
                            color: "#fff",
                            fontSize: "0.6rem",
                            px: 0.5,
                            py: 0.15,
                            borderRadius: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            opacity: day.isCurrentMonth ? 1 : 0.5,
                          }}
                        >
                          {event.name}
                        </Box>
                      ))}
                      {dayEvents.length > 3 && (
                        <Typography variant="caption" color="textSecondary" align="center" sx={{ fontSize: "0.6rem" }}>
                          +{dayEvents.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </CalendarGrid>

            <Popover
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    width: 320,
                    borderRadius: "12px",
                    p: 2,
                    mt: 1,
                    ml: 1,
                    boxShadow: "0px 8px 32px rgba(15, 23, 42, 0.15)"
                  }
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Event on {selectedDayEvents?.date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </Typography>
                <IconButton onClick={handlePopoverClose} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <RadioGroup value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 260, overflowY: "auto", mb: 2 }}>
                  {selectedDayEvents?.events.map((event) => (
                    <Box
                      key={event.id}
                      sx={{
                        border: `1px solid ${selectedEventId === event.id ? "#1976D2" : "#E0E3E7"}`,
                        bgcolor: selectedEventId === event.id ? "#F0F7FF" : "#fff",
                        borderRadius: "8px",
                        p: 1.5,
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedEventId(event.id)}
                    >
                      <FormControlLabel
                        value={event.id}
                        control={<Radio size="small" />}
                        label={
                          <Box ml={0.5}>
                            <Typography variant="body2" fontWeight={700}>{event.name}</Typography>
                            <Box display="flex" justifyContent="space-between" mt={0.5}>
                              <Typography variant="caption" color="textSecondary">
                                {parseISODate(event.startDate)?.toLocaleDateString("en-US", { month: "short", day: "numeric" })} —{" "}
                                {parseISODate(event.endDate)?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </Typography>
                              {(event.totalRooms || event.peakRooms) && (
                                <Box textAlign="right">
                                  {event.totalRooms && <Typography variant="caption" display="block">Total: {event.totalRooms.toLocaleString()}</Typography>}
                                  {event.peakRooms && <Typography variant="caption" display="block">Peak: {event.peakRooms.toLocaleString()}</Typography>}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        }
                        sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                      />
                    </Box>
                  ))}
                </Box>
              </RadioGroup>

              <StyledButton
                fullWidth
                variant="contained"
                onClick={confirmSelection}
                disabled={!selectedEventId}
              >
                Confirm Selection to See Forecast
              </StyledButton>
            </Popover>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default EventCalendarModal;